import React, { useRef } from "react";
import * as XLSX from "xlsx";
import { generatePDFFromElement } from "../../../../src/utils/invoiceUtils";

const AttendanceReport = ({ 
  isOpen, 
  onClose, 
  sectionId, 
  sectionName, 
  className,
  campusName,
  isSectionReport = true,
  startDate,
  endDate,
  userType
}) => {
  const reportRef = useRef();

  // Dummy data for attendance report
  const generateDummyAttendanceData = () => {
    const students = [
      { id: "ST001", name: "Ahmed Ali", role: "Student" },
      { id: "ST002", name: "Fatima Khan", role: "Student" },
      { id: "ST003", name: "Hassan Sheikh", role: "Student" },
      { id: "ST004", name: "Aisha Malik", role: "Student" },
      { id: "ST005", name: "Omar Farooq", role: "Student" },
      { id: "ST006", name: "Zainab Ahmed", role: "Student" },
      { id: "ST007", name: "Yusuf Hassan", role: "Student" },
      { id: "ST008", name: "Maryam Khan", role: "Student" }
    ];

    const teachers = [
      { id: "T001", name: "Dr. Sarah Johnson", role: "Teacher" },
      { id: "T002", name: "Mr. Michael Brown", role: "Teacher" },
      { id: "T003", name: "Ms. Emily Davis", role: "Teacher" },
      { id: "T004", name: "Prof. David Wilson", role: "Teacher" }
    ];

    let allMembers = [...students, ...teachers];
    
    // Filter by user type if specified
    if (userType) {
      allMembers = allMembers.filter(member => member.role === userType);
    }
    const attendanceStatuses = ["Present", "Absent", "Late", "Excused"];
    const absenceReasons = ["Sick", "Family Emergency", "Transportation Issue", "Personal", "Medical Appointment", "Weather", "Other"];

    return allMembers.map(member => {
      // Calculate date range
      const start = new Date(startDate);
      const end = new Date(endDate);
      const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      const presentDays = Math.floor(Math.random() * (totalDays - 2)) + Math.floor(totalDays * 0.7); // Random between 70%-95% attendance
      const attendancePercentage = Math.round((presentDays / totalDays) * 100);
      
      const attendanceRecords = [];
      const currentDate = new Date(start);
      
      for (let i = 0; i < totalDays; i++) {
        const status = attendanceStatuses[Math.floor(Math.random() * attendanceStatuses.length)];
        const reason = status === "Absent" ? absenceReasons[Math.floor(Math.random() * absenceReasons.length)] : "";
        
        attendanceRecords.push({
          date: currentDate.toISOString().split('T')[0],
          status,
          reason
        });
        
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return {
        ...member,
        attendancePercentage,
        weeklyAttendance: Math.floor(Math.random() * 20) + 80, // 80-100%
        monthlyAttendance: attendancePercentage,
        termWiseAttendance: Math.floor(Math.random() * 15) + 85, // 85-100%
        attendanceRecords
      };
    });
  };

  const attendanceData = generateDummyAttendanceData();
  const academicYear = "2023-24";

  const downloadPDF = () => {
    generatePDFFromElement(
      reportRef.current,
      "Attendance_Report.pdf"
    );
  };

  const downloadExcel = () => {
    if (!attendanceData || !attendanceData.length) return;

    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ["Attendance Report Summary"],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      ["Academic Year", academicYear],
      ["Date Range", `${new Date(startDate).toLocaleDateString('en-GB')} to ${new Date(endDate).toLocaleDateString('en-GB')}`],
      ["User Type Filter", userType || "All"],
      ["Report Date", new Date().toLocaleDateString('en-GB')],
      [],
      ["ID", "Full Name", "Role", "Weekly %", "Monthly %", "Term-wise %", "Overall %"]
    ];
    
    attendanceData.forEach(member => {
      summaryData.push([
        member.id,
        member.name,
        member.role,
        member.weeklyAttendance + "%",
        member.monthlyAttendance + "%",
        member.termWiseAttendance + "%",
        member.attendancePercentage + "%"
      ]);
    });
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Detailed attendance sheet
    const detailedData = [
      ["Detailed Attendance Records"],
      ["Campus", campusName],
      ["Class", className],
      ["Section", sectionName],
      ["Date Range", `${new Date(startDate).toLocaleDateString('en-GB')} to ${new Date(endDate).toLocaleDateString('en-GB')}`],
      ["User Type Filter", userType || "All"],
      [],
      ["ID", "Full Name", "Role", "Date", "Attendance Status", "Reason for Absence"]
    ];

    attendanceData.forEach(member => {
      member.attendanceRecords.forEach(record => {
        detailedData.push([
          member.id,
          member.name,
          member.role,
          record.date,
          record.status,
          record.reason
        ]);
      });
    });

    const detailedSheet = XLSX.utils.aoa_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(workbook, detailedSheet, "Detailed Records");
    
    XLSX.writeFile(workbook, "Attendance_Report.xlsx");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-gray-800">{campusName}</h1>
          <h2 className="text-lg font-semibold mt-2">Attendance Report</h2>
          <p className="text-base">{academicYear}</p>
          <p className="text-base font-medium">{className} - {sectionName}</p>
          {userType && <p className="text-base font-medium text-blue-600">Filter: {userType}s Only</p>}
          <p className="text-sm text-gray-600">Date Range: {new Date(startDate).toLocaleDateString('en-GB')} to {new Date(endDate).toLocaleDateString('en-GB')}</p>
          <p className="text-sm text-gray-600">Report Generated: {new Date().toLocaleDateString('en-GB')}</p>
        </div>

        {/* Summary Table */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Attendance Summary</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-400 p-2 text-left">ID</th>
                <th className="border border-gray-400 p-2 text-left">Full Name</th>
                <th className="border border-gray-400 p-2 text-center">Role</th>
                <th className="border border-gray-400 p-2 text-center">Weekly %</th>
                <th className="border border-gray-400 p-2 text-center">Monthly %</th>
                <th className="border border-gray-400 p-2 text-center">Term-wise %</th>
                <th className="border border-gray-400 p-2 text-center">Overall %</th>
              </tr>
            </thead>
            <tbody>
              {attendanceData.map((member, index) => (
                <tr key={index}>
                  <td className="border border-gray-400 p-2">{member.id}</td>
                  <td className="border border-gray-400 p-2">{member.name}</td>
                  <td className="border border-gray-400 p-2 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      member.role === 'Teacher' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {member.role}
                    </span>
                  </td>
                  <td className="border border-gray-400 p-2 text-center">{member.weeklyAttendance}%</td>
                  <td className="border border-gray-400 p-2 text-center">{member.monthlyAttendance}%</td>
                  <td className="border border-gray-400 p-2 text-center">{member.termWiseAttendance}%</td>
                  <td className="border border-gray-400 p-2 text-center font-semibold">
                    <span className={`${
                      member.attendancePercentage >= 90 ? 'text-green-600' :
                      member.attendancePercentage >= 80 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {member.attendancePercentage}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detailed Attendance Records */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Detailed Attendance Records</h3>
          <div className="space-y-6">
            {attendanceData.map((member, memberIndex) => (
              <div key={memberIndex} style={{ pageBreakAfter: memberIndex < attendanceData.length - 1 ? 'always' : 'auto' }}>
                <div className="mb-4">
                  <h4 className="text-md font-semibold text-gray-700">
                    {member.name} ({member.id}) - {member.role}
                  </h4>
                </div>
                
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-400 p-2 text-left">Date</th>
                      <th className="border border-gray-400 p-2 text-center">Attendance Status</th>
                      <th className="border border-gray-400 p-2 text-left">Reason for Absence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {member.attendanceRecords.map((record, recordIndex) => (
                      <tr key={recordIndex}>
                        <td className="border border-gray-400 p-2">{record.date}</td>
                        <td className="border border-gray-400 p-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs ${
                            record.status === 'Present' ? 'bg-green-100 text-green-800' :
                            record.status === 'Absent' ? 'bg-red-100 text-red-800' :
                            record.status === 'Late' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {record.status}
                          </span>
                        </td>
                        <td className="border border-gray-400 p-2">{record.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>

        {/* Attendance Statistics */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Attendance Statistics</h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="border border-gray-400 p-4">
              <h4 className="font-semibold mb-2">Students</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Students:</span>
                  <span>{attendanceData.filter(m => m.role === 'Student').length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Average Attendance:</span>
                  <span>{Math.round(attendanceData.filter(m => m.role === 'Student').reduce((acc, m) => acc + m.attendancePercentage, 0) / attendanceData.filter(m => m.role === 'Student').length)}%</span>
                </div>
              </div>
            </div>
            <div className="border border-gray-400 p-4">
              <h4 className="font-semibold mb-2">Teachers</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total Teachers:</span>
                  <span>{attendanceData.filter(m => m.role === 'Teacher').length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Average Attendance:</span>
                  <span>{Math.round(attendanceData.filter(m => m.role === 'Teacher').reduce((acc, m) => acc + m.attendancePercentage, 0) / attendanceData.filter(m => m.role === 'Teacher').length)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Lines */}
        <div className="grid grid-cols-3 gap-8 mt-12">
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Class Teacher's Signature
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Head of Department's Signature
            </div>
          </div>
          <div className="text-center">
            <div className="border-t border-gray-400 mt-8 pt-2">
              Principal's Signature
            </div>
          </div>
        </div>

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4">
          <button onClick={downloadPDF} className="bg-blue-500 px-4 py-2 rounded text-white hover:bg-blue-600">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 px-4 py-2 rounded text-white hover:bg-green-600">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 px-4 py-2 rounded text-white hover:bg-gray-500">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;
